/**
 * DeepCard — Cosmic Void v1
 *
 * A larger, premium glass card used in the "Deeper Understandings" stack.
 * Editorial Playfair title, Inter 60 % description, gold icon, optional
 * single-word shimmer on the title.
 *
 * Props:
 *   icon            : lucide icon component
 *   title           : string
 *   shimmerWord?    : string  // single word within `title` to receive the
 *                              // cv-shimmer treatment. Only the first
 *                              // matching occurrence is wrapped.
 *   description     : string
 *   onClick         : () => void
 */
import React from 'react';
import { ArrowRight } from 'lucide-react';

function renderTitle(title, shimmerWord) {
  if (!shimmerWord) return title;
  const idx = title.indexOf(shimmerWord);
  if (idx < 0) return title;
  return (
    <>
      {title.slice(0, idx)}
      <span className="cv-shimmer">{shimmerWord}</span>
      {title.slice(idx + shimmerWord.length)}
    </>
  );
}

const DeepCard = ({ icon: Icon, title, shimmerWord, description, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="cv-glass cv-font-ui"
    data-testid={testId}
    style={{
      width: '100%',
      padding: '18px 18px',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      cursor: 'pointer',
      textAlign: 'left',
    }}
  >
    {/* Gold icon orb */}
    <span
      aria-hidden="true"
      style={{
        flexShrink: 0,
        width: '42px',
        height: '42px',
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(212,164,24,0.08)',
        border: '1px solid rgba(212,164,24,0.22)',
        color: 'var(--cv-gold-primary)',
      }}
    >
      {Icon ? <Icon size={19} strokeWidth={1.5} /> : null}
    </span>

    {/* Copy */}
    <div style={{ flex: 1, minWidth: 0 }}>
      <p
        className="cv-font-hero"
        style={{
          fontSize: '16px',
          lineHeight: 1.2,
          color: 'var(--cv-text-active)',
          margin: 0,
          fontWeight: 600,
          letterSpacing: '-0.005em',
        }}
      >
        {renderTitle(title, shimmerWord)}
      </p>
      {description && (
        <p
          style={{
            fontSize: '12px',
            color: 'var(--cv-text-body)',
            margin: '4px 0 0',
            lineHeight: 1.45,
          }}
        >
          {description}
        </p>
      )}
    </div>

    <ArrowRight size={16} strokeWidth={1.5} style={{ color: 'var(--cv-text-ghost)', flexShrink: 0 }} />
  </button>
);

export default DeepCard;
